import React, { useState, useEffect } from 'react';
import { createNote, updateNote, getUsersForEditorAssignment } from '../../api/sharedNotes';
import adminApi from '../../api/admin';
import { useAuth } from '../../context/AuthContext';

const SharedNoteDialog = ({ open, onClose, onNoteCreated, onNoteUpdated, note = null }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    is_pinned: false,
    role_ids: [],
    editor_ids: []
  });
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isEditing = note !== null;
  const isAdmin = user && (user.is_staff || user.is_superuser);

  useEffect(() => {
    if (open) {
      loadRoles();
      loadUsers();
      if (isEditing) {
        setFormData({
          title: note.title,
          content: note.content,
          is_pinned: note.is_pinned,
          role_ids: note.roles.map(role => role.id),
          editor_ids: note.editors.map(editor => editor.id)
        });
      } else {
        setFormData({
          title: '',
          content: '',
          is_pinned: false,
          role_ids: [],
          editor_ids: []
        });
      }
      setError('');
    }
  }, [open, note, isEditing]);

  const loadRoles = async () => {
    try {
      const data = await adminApi.getRoles();
      setRoles(data);
    } catch (err) {
      console.error('Error loading roles:', err);
    }
  };

  const loadUsers = async () => {
    try {
      const data = await getUsersForEditorAssignment();
      setUsers(data);
    } catch (err) {
      console.error('Error loading users:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (isEditing) {
        await updateNote(note.id, formData);
        onNoteUpdated();
      } else {
        await createNote(formData);
        onNoteCreated();
      }
    } catch (err) {
      console.error('Error saving note:', err);
      setError(err.response?.data?.detail || 'Failed to save note');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleRoleChange = (roleId) => {
    setFormData(prev => ({
      ...prev,
      role_ids: prev.role_ids.includes(roleId)
        ? prev.role_ids.filter(id => id !== roleId)
        : [...prev.role_ids, roleId]
    }));
  };

  const handleEditorChange = (userId) => {
    setFormData(prev => ({
      ...prev,
      editor_ids: prev.editor_ids.includes(userId)
        ? prev.editor_ids.filter(id => id !== userId)
        : [...prev.editor_ids, userId]
    }));
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      <div 
        className="absolute inset-0 bg-black opacity-50"
        onClick={onClose}
      ></div>
      <div className="bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-lg z-10 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-ui-light-text-primary dark:text-ui-dark-text-primary">
              {isEditing ? 'Edit Note' : 'Create Shared Note'}
            </h2>
            <button
              onClick={onClose}
              className="p-2 text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary hover:text-ui-light-text-secondary dark:hover:text-ui-dark-text-secondary"
            >
              <svg className="w-6 h-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-200 rounded-md border border-red-200 dark:border-red-800">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary mb-1">
                Title
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-ui-light-border dark:border-ui-dark-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-ui-dark-background dark:text-ui-dark-text-primary"
                placeholder="Enter note title"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary mb-1">
                Content
              </label>
              <textarea
                name="content"
                value={formData.content}
                onChange={handleInputChange}
                required
                rows="12"
                className="w-full px-3 py-2 border border-ui-light-border dark:border-ui-dark-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-ui-dark-background dark:text-ui-dark-text-primary"
                placeholder="Write your note content here. You can use Markdown formatting."
              />
              <p className="mt-1 text-sm text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
                Supports Markdown formatting
              </p>
            </div>

            {isAdmin && (
              <div>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    name="is_pinned"
                    checked={formData.is_pinned}
                    onChange={handleInputChange}
                    className="rounded border-ui-light-border dark:border-ui-dark-border text-primary-600 focus:ring-primary-500 dark:bg-ui-dark-background"
                  />
                  <span className="text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary">
                    Pin this note
                  </span>
                </label>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary mb-2">
                Visible to Roles
              </label>
              <div className="space-y-2 max-h-32 overflow-y-auto border border-ui-light-border dark:border-ui-dark-border rounded-md p-3">
                {!roles.find(role => role.name.toLowerCase() === 'public') && (
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.role_ids.length === 0}
                      onChange={() => setFormData(prev => ({ ...prev, role_ids: [] }))}
                      className="rounded border-ui-light-border dark:border-ui-dark-border text-primary-600 focus:ring-primary-500 dark:bg-ui-dark-background"
                    />
                    <span className="text-sm text-ui-light-text-secondary dark:text-ui-dark-text-secondary">
                      Public (all users)
                    </span>
                  </label>
                )}
                {roles.map(role => (
                  <label key={role.id} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.role_ids.includes(role.id)}
                      onChange={() => handleRoleChange(role.id)}
                      className="rounded border-ui-light-border dark:border-ui-dark-border text-primary-600 focus:ring-primary-500 dark:bg-ui-dark-background"
                    />
                    <span className="text-sm text-ui-light-text-secondary dark:text-ui-dark-text-secondary">
                      {role.name}
                    </span>
                  </label>
                ))}
              </div>
              <p className="mt-1 text-sm text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
                Select roles that can see this note. {!roles.find(role => role.name.toLowerCase() === 'public') ? 'Leave none selected for public visibility.' : 'Select the Public role for public visibility.'}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary mb-2">
                Editors (who can edit this note)
              </label>
              <div className="space-y-2 max-h-32 overflow-y-auto border border-ui-light-border dark:border-ui-dark-border rounded-md p-3">
                {users.map(user => (
                  <label key={user.id} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.editor_ids.includes(user.id)}
                      onChange={() => handleEditorChange(user.id)}
                      className="rounded border-ui-light-border dark:border-ui-dark-border text-primary-600 focus:ring-primary-500 dark:bg-ui-dark-background"
                    />
                    <span className="text-sm text-ui-light-text-secondary dark:text-ui-dark-text-secondary">
                      {user.full_name} ({user.username})
                    </span>
                  </label>
                ))}
              </div>
              <p className="mt-1 text-sm text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
                Select users who can edit this note. Admins can always edit.
              </p>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary bg-ui-light-background dark:bg-ui-dark-background border border-ui-light-border dark:border-ui-dark-border rounded-md hover:bg-ui-light-card dark:hover:bg-ui-dark-card focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Saving...' : (isEditing ? 'Update Note' : 'Create Note')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SharedNoteDialog;